import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Check, Loader2, ChevronRight, ChevronLeft, Download, KeyRound } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Toaster } from "../components/ui/toaster";
import { ValuationProvider } from "../components/ValuationDialog";
import Seo from "../components/Seo";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEL = "h-10 w-full rounded-md border border-[#e5ddd0] bg-white px-3 text-sm text-[#16323f] focus:border-[#35a4a4] focus:outline-none";

const STEPS = ["Tenancy & Contact", "Meter Readings", "Utility Suppliers", "Forwarding Address", "Cleaning & Keys"];
const METERS = ["Electricity", "Gas", "Water", "Heating"];
const SUPPLIERS = ["Electricity", "Gas", "Water", "Council Tax", "Broadband"];
const STATUS = ["Settled", "Final bill pending", "Not yet arranged"];

const Field = ({ label, hint, children, required }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#7a716a]">{label}{required && " *"}</Label>
    {children}
    {hint && <p className="text-xs text-[#7a716a]">{hint}</p>}
  </div>
);

function Content() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [savedId, setSavedId] = useState("");

  const [form, setForm] = useState({
    tenancy_end_date: "", property_address: "", tenant_names: "", contact_phone: "", contact_email: "",
    meters: METERS.map((u) => ({ utility: u, serial: "", reading: "", date: "" })),
    suppliers: SUPPLIERS.map((u) => ({ utility: u, supplier: "", account: "", status: "" })),
    forwarding_address: "", forwarding_phone: "", forwarding_email: "",
    professional_cleaned: false, cleaning_contractor: "", cleaning_date: "", invoice_ref: "",
    keys_present: false, key_return_location: "", additional_notes: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setMeter = (i, k, v) => setForm((f) => { const a = [...f.meters]; a[i] = { ...a[i], [k]: v }; return { ...f, meters: a }; });
  const setSup = (i, k, v) => setForm((f) => { const a = [...f.suppliers]; a[i] = { ...a[i], [k]: v }; return { ...f, suppliers: a }; });

  const stepValid = useMemo(() => {
    if (step === 1) return form.property_address && form.tenant_names && form.contact_email;
    if (step === 4) return !!form.forwarding_address;
    return true;
  }, [step, form]);

  const next = () => {
    if (!stepValid) { toast({ title: "A few details still need attention", description: "Please complete the required fields to continue.", variant: "destructive" }); return; }
    setStep((s) => Math.min(5, s + 1)); window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const submit = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/move-outs`, form);
      setSavedId(res.data?.id || "");
      setDone(true); window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast({ title: "Something went wrong", description: "Please try again in a moment.", variant: "destructive" });
    } finally { setLoading(false); }
  };

  return (
    <div className="App">
      <Seo title="Move-Out Checklist | New Space Lettings" description="End-of-tenancy move-out and deposit checklist for New Space tenants." path="/move-out" />
      <Header />

      <section className="relative overflow-hidden bg-[#16323f] pt-32 pb-14 lg:pt-40">
        <div className="mx-auto max-w-[900px] px-5 text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#d8b074]">Move-out &amp; deposit toolkit</p>
          <h1 className="mt-4 font-display text-4xl font-700 uppercase tracking-wide text-white lg:text-5xl">Your end-of-tenancy<br />move-out checklist.</h1>
          <p className="mx-auto mt-4 max-w-lg text-white/80">Confirm your final meter readings, utility suppliers, forwarding address and cleaning — everything we need to progress your deposit return.</p>
        </div>
      </section>

      <section className="bg-[#faf7f0] py-14 lg:py-20">
        <div className="mx-auto max-w-[1040px] px-5">
          {done ? (
            <div className="mx-auto max-w-xl rounded-2xl border border-[#e5ddd0] bg-white p-10 text-center shadow-lg" data-testid="moveout-success">
              <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#35a4a4] text-white"><Check size={34} /></div>
              <h2 className="mt-5 font-display text-2xl font-600 text-[#16323f]">Checklist submitted</h2>
              <p className="mx-auto mt-3 max-w-md text-sm text-[#5b5450]">Thank you — your move-out details have been received. Our team will review them alongside the check-out inventory to progress your deposit return.</p>
              <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.3em] text-[#c9a25e]">Reference</p>
              <p className="text-lg font-700 text-[#16323f]" data-testid="moveout-reference">{savedId.slice(0, 8).toUpperCase()}</p>
              <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
                {savedId && (
                  <a href={`${API}/move-outs/${savedId}/pdf`} target="_blank" rel="noopener noreferrer" data-testid="download-moveout-pdf-button"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#35a4a4] px-9 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#2b8686]">
                    <Download size={16} /> Download my copy
                  </a>
                )}
                <button onClick={() => navigate("/")} data-testid="moveout-home-button"
                  className="rounded-full bg-[#16323f] px-9 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#ec008c]">Back to main site</button>
              </div>
            </div>
          ) : (
            <div className="grid gap-8 lg:grid-cols-[280px_1fr]">
              <aside className="lg:sticky lg:top-28 lg:self-start">
                <div className="rounded-2xl border border-[#e5ddd0] bg-white p-6 shadow-sm">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#c9a25e]">Move-out</p>
                  <ol className="mt-4 space-y-1">
                    {STEPS.map((label, i) => {
                      const n = i + 1, active = n === step, ds = n < step;
                      return (
                        <li key={label} className={`flex items-center gap-3 rounded-lg px-2 py-2 ${active ? "bg-[#16323f]/5" : ""}`}>
                          <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-700 ${active ? "bg-[#ec008c] text-white" : ds ? "bg-[#35a4a4] text-white" : "bg-[#eee6da] text-[#7a716a]"}`}>
                            {ds ? <Check size={14} strokeWidth={3} /> : String(n).padStart(2, "0")}
                          </span>
                          <span className={`text-sm ${active ? "font-600 text-[#16323f]" : "text-[#7a716a]"}`}>{label}</span>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              </aside>

              <div className="rounded-2xl border border-[#e5ddd0] bg-white p-7 shadow-sm lg:p-9">
                <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#c9a25e]">Step {step} of {STEPS.length}</p>
                <h2 className="mt-1 font-display text-2xl font-600 uppercase tracking-wide text-[#16323f]">{STEPS[step - 1]}</h2>
                <span className="mt-3 block h-[3px] w-14 rounded-full bg-[#eaa32a]" />

                {step === 1 && (
                  <div className="mt-6 space-y-5">
                    <Field label="Tenancy end date"><Input type="date" value={form.tenancy_end_date} onChange={(e) => set("tenancy_end_date", e.target.value)} data-testid="mo-end-date" /></Field>
                    <Field label="Property address" required><Input value={form.property_address} onChange={(e) => set("property_address", e.target.value)} placeholder="Flat 4, 14 Oakwood Court, London SW11 6QT" data-testid="mo-property-address" /></Field>
                    <Field label="Tenant name(s)" required><Input value={form.tenant_names} onChange={(e) => set("tenant_names", e.target.value)} placeholder="Full name(s) of tenant(s)" data-testid="mo-tenant-names" /></Field>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field label="Contact phone"><Input type="tel" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} placeholder="07700 900000" data-testid="mo-phone" /></Field>
                      <Field label="Contact email" required><Input type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} placeholder="you@example.com" data-testid="mo-email" /></Field>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="mt-6 space-y-4">
                    <p className="text-sm text-[#7a716a]">Please provide final readings for each meter on your end-of-tenancy date.</p>
                    {form.meters.map((m, i) => (
                      <div key={m.utility} className="rounded-xl border border-[#e5ddd0] p-5" data-testid={`mo-meter-${i}`}>
                        <p className="mb-3 text-sm font-600 text-[#16323f]">{m.utility}</p>
                        <div className="grid gap-4 sm:grid-cols-3">
                          <Field label="Meter serial"><Input value={m.serial} onChange={(e) => setMeter(i, "serial", e.target.value)} placeholder="Serial no." data-testid={`mo-meter-${i}-serial`} /></Field>
                          <Field label="Final reading"><Input value={m.reading} onChange={(e) => setMeter(i, "reading", e.target.value)} placeholder="e.g. 34212" data-testid={`mo-meter-${i}-reading`} /></Field>
                          <Field label="Reading date"><Input type="date" value={m.date} onChange={(e) => setMeter(i, "date", e.target.value)} data-testid={`mo-meter-${i}-date`} /></Field>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {step === 3 && (
                  <div className="mt-6 space-y-4">
                    <p className="text-sm text-[#7a716a]">Confirm who your providers were and whether final bills have been settled.</p>
                    {form.suppliers.map((s, i) => (
                      <div key={s.utility} className="rounded-xl border border-[#e5ddd0] p-5" data-testid={`mo-supplier-${i}`}>
                        <p className="mb-3 text-sm font-600 text-[#16323f]">{s.utility}</p>
                        <div className="grid gap-4 sm:grid-cols-3">
                          <Field label="Supplier"><Input value={s.supplier} onChange={(e) => setSup(i, "supplier", e.target.value)} placeholder="Provider name" data-testid={`mo-supplier-${i}-name`} /></Field>
                          <Field label="Account no."><Input value={s.account} onChange={(e) => setSup(i, "account", e.target.value)} placeholder="Account ref" data-testid={`mo-supplier-${i}-account`} /></Field>
                          <Field label="Settlement status">
                            <select className={SEL} value={s.status} onChange={(e) => setSup(i, "status", e.target.value)} data-testid={`mo-supplier-${i}-status`}>
                              <option value="">Select…</option>
                              {STATUS.map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                          </Field>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {step === 4 && (
                  <div className="mt-6 space-y-5">
                    <Field label="Forwarding address (for deposit release)" required><Textarea rows={3} value={form.forwarding_address} onChange={(e) => set("forwarding_address", e.target.value)} placeholder="Your new address" data-testid="mo-forwarding-address" /></Field>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field label="Contact phone"><Input type="tel" value={form.forwarding_phone} onChange={(e) => set("forwarding_phone", e.target.value)} placeholder="07700 900000" data-testid="mo-forwarding-phone" /></Field>
                      <Field label="Contact email"><Input type="email" value={form.forwarding_email} onChange={(e) => set("forwarding_email", e.target.value)} placeholder="you@example.com" data-testid="mo-forwarding-email" /></Field>
                    </div>
                  </div>
                )}

                {step === 5 && (
                  <div className="mt-6 space-y-5">
                    <label htmlFor="mo-cleaned" className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${form.professional_cleaned ? "border-[#35a4a4] bg-[#35a4a4]/5" : "border-[#e5ddd0]"}`}>
                      <Checkbox id="mo-cleaned" data-testid="mo-cleaned" checked={form.professional_cleaned} onCheckedChange={(v) => set("professional_cleaned", !!v)} className="mt-0.5" />
                      <span className="text-sm leading-relaxed text-[#5b5450]">I confirm the property has been professionally cleaned to the standard required (including carpets, appliances, windows and bathrooms).</span>
                    </label>
                    <div className="grid gap-5 sm:grid-cols-3">
                      <Field label="Cleaning contractor"><Input value={form.cleaning_contractor} onChange={(e) => set("cleaning_contractor", e.target.value)} placeholder="Company name" data-testid="mo-contractor" /></Field>
                      <Field label="Cleaning date"><Input type="date" value={form.cleaning_date} onChange={(e) => set("cleaning_date", e.target.value)} data-testid="mo-clean-date" /></Field>
                      <Field label="Invoice / receipt reference"><Input value={form.invoice_ref} onChange={(e) => set("invoice_ref", e.target.value)} placeholder="Invoice ref (if available)" data-testid="mo-invoice" /></Field>
                    </div>
                    <label htmlFor="mo-keys" className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${form.keys_present ? "border-[#35a4a4] bg-[#35a4a4]/5" : "border-[#e5ddd0]"}`}>
                      <Checkbox id="mo-keys" data-testid="mo-keys" checked={form.keys_present} onCheckedChange={(v) => set("keys_present", !!v)} className="mt-0.5" />
                      <span className="text-sm leading-relaxed text-[#5b5450]">All keys, fobs, meter keys and cards are present and ready to be handed over.</span>
                    </label>
                    <Field label="Key return location / method"><Input value={form.key_return_location} onChange={(e) => set("key_return_location", e.target.value)} placeholder="e.g. Handed to inventory clerk at check-out" data-testid="mo-key-return" /></Field>
                    <Field label="Additional notes"><Textarea rows={3} value={form.additional_notes} onChange={(e) => set("additional_notes", e.target.value)} placeholder="Anything else we should know" data-testid="mo-notes" /></Field>
                  </div>
                )}

                <div className="mt-8 flex items-center justify-between border-t border-[#eee6da] pt-6">
                  <button type="button" onClick={back} disabled={step === 1} data-testid="mo-back-button"
                    className="inline-flex items-center gap-2 rounded-full border border-[#e5ddd0] px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#5b5450] transition-colors hover:border-[#16323f] disabled:opacity-40">
                    <ChevronLeft size={16} /> Back
                  </button>
                  {step < 5 ? (
                    <button type="button" onClick={next} data-testid="mo-continue-button"
                      className="inline-flex items-center gap-2 rounded-full bg-[#eaa32a] px-8 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#d8901a]">
                      Continue <ChevronRight size={16} />
                    </button>
                  ) : (
                    <button type="button" onClick={submit} disabled={loading} data-testid="mo-submit-button"
                      className="inline-flex items-center gap-2 rounded-full bg-[#ec008c] px-8 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#c60074] disabled:opacity-70">
                      {loading ? (<><Loader2 size={16} className="animate-spin" /> Submitting…</>) : (<><KeyRound size={16} /> Submit checklist</>)}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
      <Toaster />
    </div>
  );
}

export default function MoveOut() {
  return (
    <ValuationProvider>
      <Content />
    </ValuationProvider>
  );
}
